/* eslint-disable jsx-a11y/alt-text */
import {
  memo,
  useContext,
  useCallback,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import styles from './Controls.module.css';
import { faHammer } from '@fortawesome/free-solid-svg-icons';
import {
  AllInputDataContext,
  SetSliderValuesContext,
  SliderValuesContext,
} from '../../config';
import { Button, Radio, Typography } from 'antd';
import { playColors } from '../../constants';
import { DownCircleOutlined, LeftCircleOutlined } from '@ant-design/icons';
import { TechnologyData } from '../../types/AllInputData.types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIsLandscape } from '../../../../useEffects/useIsLandscape';
import { SizeIcon } from './SizeIcon';
import { isDesktop, isMobileOnly, isTablet } from 'react-device-detect';

const { Text, Title } = Typography;

const sizing = {
  off: 0,
  small: 0.25,
  medium: 0.5,
  large: 1,
};
export const valueToSize = (value: number, capMax: number) => {
  const percent = value / capMax;

  if (percent >= 0.1 && percent < 0.4) return 'small';
  if (percent >= 0.4 && percent < 0.8) return 'medium';
  if (percent >= 0.8) {
    return 'large';
  }
  return 'off';
};

export const MEControls = memo(
  ({
    incrementAmmount,
    controlView,
    setControlView,
    welcomeJourneyStep,
    processWelcomeJourney,
  }: {
    incrementAmmount: number;
    controlView: 'full-screen' | 'vertical' | 'slim';
    setControlView: Dispatch<
      SetStateAction<'full-screen' | 'vertical' | 'slim'>
    >;
    welcomeJourneyStep: number;
    processWelcomeJourney: () => void;
  }) => {
    const sliderValues = useContext(SliderValuesContext);
    const setSliderValues = useContext(SetSliderValuesContext);
    const [selectedTech, setSelectedTech] = useState<TechnologyData>();
    const { technologyData } = useContext(AllInputDataContext);
    const isLandscape = useIsLandscape();

    const updateSliderValues = useCallback(
      (value, techIndexNumber) => {
        setSliderValues((sliderValues) => {
          let values = [...sliderValues];
          values[techIndexNumber] = value;
          return values;
        });
      },
      [setSliderValues]
    );

    return (
      <div className={styles.allSliderControls}>
        <div
          className={styles.innerContainer}
          style={{
            width: '100%',
            overflow: 'auto',
            height:
              controlView === 'vertical' && !isTablet
                ? window.innerHeight
                : 'unset',
          }}
        >
          {welcomeJourneyStep !== 99 && controlView === 'full-screen' && (
            <div style={{ paddingLeft: 10, paddingBottom: 10 }}>
              <Title level={2}>Create Your Energy Grid</Title>
              <Text>
                Choose the size of each power plant.{' '}
                <strong>Scroll down for more options</strong>
              </Text>
            </div>
          )}
          {controlView !== 'full-screen' && selectedTech && !isLandscape && (
            <>
              {controlView !== 'vertical' && (
                <LeftCircleOutlined
                  style={{
                    alignSelf: 'flex-end',
                    fontSize: 23,
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    color: playColors.primaryAlt,
                  }}
                  onClick={() => setSelectedTech(undefined)}
                />
              )}
              <DownCircleOutlined
                style={{
                  alignSelf: 'flex-end',
                  fontSize: 23,
                  position: 'absolute',
                  bottom: 5,
                  right: 5,
                  transform: controlView === 'vertical' ? 'rotate(180deg)' : '',
                  color: playColors.primaryAlt,
                }}
                onClick={() =>
                  setControlView((prev) =>
                    prev === 'slim' ? 'vertical' : 'slim'
                  )
                }
              />
            </>
          )}
          {selectedTech ||
          controlView === 'vertical' ||
          controlView === 'full-screen'
            ? technologyData.map(
                (item, index) =>
                  (controlView === 'vertical' ||
                    controlView === 'full-screen' ||
                    item.id === selectedTech?.id) &&
                  item.name !== 'Blackout' && (
                    <div
                      className={styles.technologyContainer}
                      key={item.id}
                      style={{
                        height:
                          controlView === 'vertical' ||
                          controlView === 'full-screen'
                            ? 95
                            : '100%',

                        marginLeft:
                          isDesktop || (isLandscape && isMobileOnly)
                            ? isLandscape && isMobileOnly
                              ? 5
                              : 0
                            : 10,
                      }}
                    >
                      <hr
                        className={styles.technologyLeftLine}
                        style={{
                          backgroundColor: playColors.black,
                        }}
                      />
                      <div
                        className={styles.technologyImageDiv}
                        style={
                          isLandscape && isMobileOnly ? { minWidth: 70 } : {}
                        }
                      >
                        <img
                          style={
                            isLandscape && isMobileOnly
                              ? { maxWidth: 70, padding: '5px 3px' }
                              : {}
                          }
                          src={`/images/plant/${item.name}.png`}
                        />
                      </div>

                      <div className={styles.technologyContent}>
                        <div className={styles.technologyContentTextContainer}>
                          <Text>
                            <b>{item.name} Plant </b>
                          </Text>
                        </div>
                        <Radio.Group
                          value={valueToSize(
                            sliderValues[item.id - 1],
                            item.capMax
                          )}
                          onChange={(e) =>
                            updateSliderValues(
                              //@ts-ignore
                              (sizing[e.target.value] as number) * item.capMax,
                              item.id - 1
                            )
                          }
                          optionType="button"
                          size="small"
                          buttonStyle="solid"
                        >
                          {['Off', 'Small', 'Medium', 'Large'].map((size) => (
                            <Radio.Button value={size.toLowerCase()}>
                              {size}
                            </Radio.Button>
                          ))}
                        </Radio.Group>
                        <Text>{item.userDescription}</Text>
                      </div>
                    </div>
                  )
              )
            : null}

          <div
            //SLIM VIEW
            className={styles.slimControlsContainer}
            style={{
              display:
                selectedTech ||
                controlView === 'vertical' ||
                controlView === 'full-screen'
                  ? 'none'
                  : 'flex',
            }}
          >
            {technologyData.map(
              (item, index) =>
                item.name !== 'Blackout' && (
                  <div
                    key={item.id}
                    className={styles.techSlimIconContainer}
                    style={{ borderBottomColor: playColors.black }}
                    onClick={() => {
                      setSelectedTech(item);
                    }}
                  >
                    <img
                      src={`/images/plant/${item.name}.png`}
                      height={70}
                      width="auto"
                      style={{ padding: '0px 5px' }}
                    />

                    <Text style={{ fontWeight: 'bold' }}>
                      <SizeIcon
                        size={valueToSize(
                          sliderValues[item.id - 1],
                          item.capMax
                        )}
                        color={playColors.primaryAlt}
                      />
                    </Text>
                  </div>
                )
            )}
          </div>
          {welcomeJourneyStep !== 99 && controlView === 'full-screen' && (
            <div style={{ padding: 10, animation: 'fadeIn 5s' }}>
              <Title level={3}>Ready to See the Impact?</Title>
              <Text>
                Discover how your choices affect energy costs, emissions, and
                security
              </Text>

              <br />
              <br />
              <Button
                onClick={() => {
                  processWelcomeJourney();
                  setControlView(
                    !isDesktop && !isLandscape ? 'slim' : 'vertical'
                  );
                }}
                style={{ marginBottom: 10 }}
                type="primary"
                icon={<FontAwesomeIcon icon={faHammer} />}
                size="large"
              >
                Build my power grid
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
);
